import { Link } from "@reach/router";
import React, { useContext, useEffect, useMemo, useRef } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { BrandsContext } from "../../context/BrandsContext";
import { MilestonesContext } from "../../context/MilestonesContext";
import {
  PrevButton,
  NextButton,
  usePrevNextButtons,
} from "../global/EmblaCarouselArrowButtons";
/**
 * Teaser for TBM Rewards — copy distilled from RewardsHero + RewardsFeatures
 * (see views/LandingRewards.js).
 */
const BRAND_CAROUSEL_OPTIONS = { align: "start", loop: true };

const REWARDS_POINTS = [
  {
    title: "Progreso visible",
    desc: "Mide tu evolución y mantente inspirada y comprometida con tus entrenamientos.",
  },
  {
    title: "Recompensas",
    desc: "Avanza y accede a premios, descuentos, regalos y experiencias de marcas de bienestar.",
  },
  {
    title: "Motivación continua",
    desc: "Celebra tus logros junto a otras alumnas de la Comunidad TBM.",
  },
];

export default function LandingOnlineRewardsTeaser() {
  const containerRef = useRef(null);
  const { brands, getBrands } = useContext(BrandsContext);
  const { milestones, getMilestones } = useContext(MilestonesContext);

  const [emblaRef, emblaApi] = useEmblaCarousel(BRAND_CAROUSEL_OPTIONS);
  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi);

  const starCount = useMemo(() => {
    if (!milestones?.length) return 5;
    const maxId = Math.max(...milestones.map((m) => m.milestone_id || 0), 0);
    return maxId > 0 ? Math.min(maxId, 8) : 5;
  }, [milestones]);

  const stars = useMemo(
    () => Array.from({ length: starCount }, (_, i) => i),
    [starCount],
  );

  useEffect(() => {
    if (!brands) {
      getBrands();
    }
    if (!milestones) {
      getMilestones();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (emblaApi) emblaApi.reInit();
  }, [emblaApi, brands]);

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0.25,
    };

    const handleIntersection = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.remove("opacity-0");
          entry.target.classList.add("animate-fade-in");
        }
      });
    };

    const observer = new IntersectionObserver(
      handleIntersection,
      observerOptions,
    );
    const node = containerRef.current;
    if (node) observer.observe(node);

    return () => {
      if (node) observer.unobserve(node);
    };
  }, []);

  return (
    <section
      id="rewards"
      className="landing-online-rewards-teaser"
      aria-labelledby="landing-rewards-teaser-title"
      style={{ backgroundColor: "#E4E9F4" }}
    >
      <div
        ref={containerRef}
        className="landing-online-rewards-teaser__inner opacity-0 w-100"
      >
        <p className="text-center mt-1 mb-2 landing-online-rewards-teaser__brand">
          TBM REWARDS
        </p>
        <h2
          id="landing-rewards-teaser-title"
          className="online-title text-dark text-center mb-2 mb-md-3"
        >
          Tu constancia tiene recompensa
        </h2>
        <p className="text-center mb-3 mb-md-4 px-2 landing-online-rewards-teaser__lead">
          Un programa diseñado para reconocer tu disciplina y motivarte a seguir
          avanzando. A medida que completas clases, registras tu progreso y
          participas activamente en la plataforma, acumulas recompensas que
          celebran tu esfuerzo y compromiso dentro de TBM.
        </p>

        <div
          className="landing-online-rewards-teaser__stars-row"
          role="img"
          aria-label="Niveles de recompensa"
        >
          {stars.map((i) => (
            <div
              key={i}
              className="landing-online-rewards-teaser__star"
              aria-hidden="true"
            />
          ))}
        </div>

        <div className="row align-items-center g-4 g-lg-5 mb-4 mb-lg-5">
          <div className="col-12 col-md-5 text-center text-md-start">
            <img
              src="/img/rewards-hero-edit.png"
              alt="TBM team — TBM Rewards"
              className="landing-online-rewards-teaser__img"
            />
          </div>
          <div className="col-12 col-md-7">
            <div className="row g-3">
              {REWARDS_POINTS.map((item) => (
                <div key={item.title} className="col-12">
                  <div className="landing-online-rewards-teaser__point h-100">
                    <h3 className="landing-online-rewards-teaser__point-title">
                      {item.title}
                    </h3>
                    <p className="landing-online-rewards-teaser__point-desc">
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {brands && brands.length > 0 ? (
          <div
            className="landing-online-rewards-teaser__brands text-center"
            aria-label="Nuestras marcas"
          >
            <h3 className="brands-title mb-2 mb-md-3">Nuestras marcas</h3>
            <p className="brands-desc mb-3 mb-md-4">
              Conoce las marcas participantes
            </p>
            <section className="carousel__rewards-brands">
              <div className="rewards-brands__viewport" ref={emblaRef}>
                <div className="rewards-brands__container">
                  {brands.slice(1).map((brand, index) => (
                    <div
                      key={brand.brand_id ?? index}
                      className="brand-card rewards-brands__slide d-flex flex-column align-items-center justify-content-center landing-online-rewards-teaser__brand-slide"
                    >
                      <img
                        alt={brand.name || "Marca"}
                        src={brand.file?.src || brand.logo}
                        width="auto"
                        style={{ maxHeight: "100px", minWidth: "100px" }}
                        className="landing-online-rewards-teaser__brand-img"
                        draggable={false}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="rewards-brands__controls">
                <div className="rewards-brands__buttons">
                  <PrevButton
                    onClick={onPrevButtonClick}
                    disabled={prevBtnDisabled}
                  />
                  <NextButton
                    onClick={onNextButtonClick}
                    disabled={nextBtnDisabled}
                  />
                </div>
              </div>
            </section>
          </div>
        ) : null}

        <div className="text-center mt-4 mt-md-5">
          <Link to="/rewards/" className="btn btn-primary">
            Descubre TBM Rewards
            <i className="fas fa-arrow-right" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
}
